import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getLocations, getGalleryItems } from '../services/api';
import { ChevronDown, ChevronUp, MapPin, Mail, Phone, Instagram, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { medicalConditions } from '../mockData';
import { Heart, Baby, Activity, AlertCircle, XCircle, AlertTriangle, Shield, Slash } from 'lucide-react';
import axios from 'axios';
import BookingForm from '../components/BookingForm';

const iconMap = {
  Heart, 
  Baby, 
  Activity, 
  AlertCircle, 
  XCircle, 
  AlertTriangle, 
  Shield, 
  Slash
};

const faqs = [
  {
    question: "Can we eat something before Bungy jump?",
    answer: "Yes, you can eat before any of the activities, as long as you are comfortable. We suggest to have light meal or empty stomach before the jump."
  },
  {
    question: "What age do I have to be to Bungy Jump?",
    answer: "The minimum age requirement for bungee jumping is generally, participants must be at least 10 years old and maximum 65 years with good health. It's important to note that bungee jumping is a physically demanding activity that requires a certain level of fitness and health."
  },
  {
    question: "Can my friends and family come watch me?",
    answer: "In most cases, friends and family members are allowed to watch you bungee jumping. However, it's important to note that the specific rules and regulations regarding spectators can vary depending on the bungee jumping operator and location."
  },
  {
    question: "Is there any food facility available at jump zone?",
    answer: "We have a small cafeteria which offers you some available snacks."
  },
  {
    question: "What about photography/Videography? Can we use our own Go pro camera?",
    answer: "We do not allow any mobile or outsider cameras at our jump premises. We offer still photography as well as 4K high quality videos, available for purchase at the end of the jumps."
  },
  {
    question: "What happens in case of bad weather?",
    answer: "Bungy Jump may be postponed or rescheduled due to adverse weather conditions for safety reasons. We also provide option to refund in such case."
  },
  {
    question: "What are the timings?",
    answer: "We are open daily between 9:00 AM to 6:00 PM."
  }
];

const LocationDetailPage = () => {
  const { locationName } = useParams();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [videoUrl, setVideoUrl] = useState('https://cdn.coverr.co/videos/coverr-bungee-jumping-8521/1080p.mp4');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Location-specific Instagram handles
  const locationInstagramHandles = {
    'goa': 'https://www.instagram.com/indyabungygoa/',
    'delhi-ncr': 'https://www.instagram.com/indyabungydelhi/',
    'coorg': 'https://www.instagram.com/indyabungy/',
    'mumbai': 'https://www.instagram.com/indyabungy/',
    'bangalore': 'https://www.instagram.com/indyabungy/',
    'hyderabad': 'https://www.instagram.com/indyabungy/',
    'kerala': 'https://www.instagram.com/indyabungy/',
    'jaipur': 'https://www.instagram.com/indyabungy/'
  };

  const getInstagramHandle = () => {
    return locationInstagramHandles[locationName] || 'https://www.instagram.com/indyabungy/';
  };

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const locations = await getLocations();
        const found = locations.find(
          loc => loc.name.toLowerCase().replace(' ', '-') === locationName
        );
        setLocation(found);
        
        // Fetch gallery images for this location
        if (found) {
          const gallery = await getGalleryItems({ location: found.name });
          setGalleryImages(gallery);
          
          // Fetch location video
          const API = process.env.REACT_APP_BACKEND_URL || '';
          try {
            const videoResponse = await axios.get(`${API}/api/videos`);
            // Find video for this location or fall back to active location type video
            const locationVideo = videoResponse.data.find(
              video => video.type === 'location' && video.isActive && 
              (video.locationId === found._id || video.location === found.name)
            );
            if (locationVideo && locationVideo.url) {
              setVideoUrl(locationVideo.url);
            }
          } catch (error) {
            console.error('Error fetching location video:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching location:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLocation();
  }, [locationName]);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const showPrevious = () => {
    const newIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const showNext = () => {
    const newIndex = (currentIndex + 1) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-2xl">Location not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Video Section */}
      <div className="relative h-screen overflow-hidden">
        <video
          key={videoUrl}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          poster="/bg-new.png"
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/42 via-black/28 to-black"></div>
        
        <div className="relative z-10 h-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black text-white tracking-tighter animate-fade-in-up">
              {location.name.toUpperCase()}. SHOUTS
            </h1>
            <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-200 tracking-tighter animate-fade-in-up animation-delay-200">
              BUNGEE
            </h1>
            <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-white/90 tracking-wide animate-fade-in-up animation-delay-400">
              FUN. BEGINS HERE.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-20 bg-gradient-to-b from-gray-900 to-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={location.image}
                alt={location.name}
                className="w-full h-96 object-cover"
              />
            </div>

            {/* Stats */}
            <div className="space-y-8">
              <div className="grid grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Over</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-black text-yellow-400 mb-0.5 sm:mb-1">{location.overValue || '44,000+'}</p>
                  <p className="text-xs sm:text-sm text-gray-400">{location.overLabel || 'Safe Jumps'}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Rated</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-black text-yellow-400 mb-0.5 sm:mb-1">{location.ratedValue || '5 Star'}</p>
                  <p className="text-xs sm:text-sm text-gray-400">{location.ratedLabel || 'Experience'}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Since</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-black text-yellow-400 mb-0.5 sm:mb-1">{location.sinceValue || '7 Year'}</p>
                  <p className="text-xs sm:text-sm text-gray-400">{location.sinceLabel || 'Best Service'}</p>
                </div>
              </div>

              <p className="text-gray-300 leading-relaxed">
                Our bungee jumping company, based in {location.name}, has proudly completed more than 44,000 safe and exhilarating jumps, making us one of the most trusted names in adventure sports. We are fully certified by the Ministry of Tourism and the Government, ensuring that all safety protocols and guidelines are strictly adhered to.
              </p>

              <a
                href={getInstagramHandle()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white px-6 py-3 rounded-full font-bold hover:from-pink-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105"
              >
                <Instagram size={20} />
                <span>Follow us on Instagram</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Packages Section */}
      {location.packages && location.packages.length > 0 ? (
        <div className="py-20 bg-gradient-to-b from-gray-950 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
                Packages
              </h2>
              <p className="text-xl text-gray-400">
                Choose your adventure package
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {location.packages.map((pkg, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-2xl p-8 hover:border-yellow-400/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <h3 className="text-3xl font-black text-yellow-400 mb-4">
                    {pkg.name}
                  </h3>
                  <p className="text-4xl font-black text-white mb-6">
                    ₹{pkg.price}
                  </p>
                  <ul className="space-y-3 text-gray-300 mb-6">
                    {pkg.features && pkg.features.length > 0 && pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-yellow-400 mr-2">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                    {pkg.inclusions && pkg.inclusions.length > 0 && pkg.inclusions.map((inclusion, i) => (
                      <li key={`inc-${i}`} className="flex items-start">
                        <span className="text-yellow-400 mr-2">✓</span>
                        <span>{inclusion}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => {
                      setSelectedPackage(pkg);
                      setShowBookingForm(true);
                      setTimeout(() => {
                        document.getElementById('booking-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    BOOK NOW
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="py-20 bg-gradient-to-b from-gray-950 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
                Packages
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                Choose your adventure package
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Default Package 1 - Solo Jump */}
              <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-2xl p-8 hover:border-yellow-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <h3 className="text-3xl font-black text-yellow-400 mb-4">
                  Solo Jump
                </h3>
                <p className="text-4xl font-black text-white mb-6">
                  ₹3,500
                </p>
                <ul className="space-y-3 text-gray-300 mb-6">
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>Safety Equipment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>Professional Guidance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>Certificate</span>
                  </li>
                </ul>
                <button
                  onClick={() => {
                    setSelectedPackage({ name: 'Solo Jump', price: 3500 });
                    setShowBookingForm(true);
                    setTimeout(() => {
                      document.getElementById('booking-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  BOOK NOW
                </button>
              </div>

              {/* Default Package 2 - Jump + Video */}
              <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-2xl p-8 hover:border-yellow-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <h3 className="text-3xl font-black text-yellow-400 mb-4">
                  Jump + Video
                </h3>
                <p className="text-4xl font-black text-white mb-6">
                  ₹4,500
                </p>
                <ul className="space-y-3 text-gray-300 mb-6">
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>Safety Equipment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>Professional Guidance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>HD Video Recording</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>Certificate</span>
                  </li>
                </ul>
                <button
                  onClick={() => {
                    setSelectedPackage({ name: 'Jump + Video', price: 4500 });
                    setShowBookingForm(true);
                    setTimeout(() => {
                      document.getElementById('booking-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  BOOK NOW
                </button>
              </div>

              {/* Default Package 3 - Premium Package */}
              <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-2xl p-8 hover:border-yellow-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <h3 className="text-3xl font-black text-yellow-400 mb-4">
                  Premium Package
                </h3>
                <p className="text-4xl font-black text-white mb-6">
                  ₹5,500
                </p>
                <ul className="space-y-3 text-gray-300 mb-6">
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>Safety Equipment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>Professional Guidance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>4K Video + Photos</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>T-Shirt</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">✓</span>
                    <span>Certificate</span>
                  </li>
                </ul>
                <button
                  onClick={() => {
                    setSelectedPackage({ name: 'Premium Package', price: 5500 });
                    setShowBookingForm(true);
                    setTimeout(() => {
                      document.getElementById('booking-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  BOOK NOW
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Form Section - Shows when BOOK NOW is clicked */}
      {showBookingForm && (
        <div id="booking-form-section" className="py-20 bg-gradient-to-b from-black to-gray-900 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                Book Your Adventure
              </h2>
              {selectedPackage && (
                <p className="text-xl text-yellow-400 mb-2">
                  Selected: {selectedPackage.name}  ₹{selectedPackage.price}
                </p>
              )}
              <p className="text-xl text-gray-400">
                Reserve your spot and experience the thrill of a lifetime
              </p>
              <button
                onClick={() => setShowBookingForm(false)}
                className="mt-4 text-gray-400 hover:text-white transition-colors text-sm"
              >
                ✕ Close Booking Form
              </button>
            </div>
            <BookingForm location={location} selectedPackage={selectedPackage} />
          </div>
        </div>
      )}

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <div className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
                Gallery
              </h2>
              <p className="text-xl text-gray-400">
                Captured moments of pure adrenaline
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryImages.map((image, index) => (
                <div
                  key={index}
                  className="relative group cursor-pointer overflow-hidden rounded-lg aspect-square"
                  onClick={() => openLightbox(image, index)}
                >
                  <img
                    src={image.url}
                    alt={image.title || `Gallery ${index + 1}`}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-42 group-hover:opacity-56 transition-opacity duration-300"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-yellow-400 transition-colors z-50"
          >
            <X size={32} />
          </button>
          
          <button
            onClick={showPrevious}
            className="absolute left-4 text-white hover:text-yellow-400 transition-colors z-50"
          >
            <ChevronLeft size={48} />
          </button>
          
          <button
            onClick={showNext}
            className="absolute right-4 text-white hover:text-yellow-400 transition-colors z-50"
          >
            <ChevronRight size={48} />
          </button>

          <img
            src={selectedImage.url}
            alt={selectedImage.title}
            className="max-w-full max-h-[90vh] object-contain"
          />
        </div>
      )}

      {/* Medical Conditions */}
      <div className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
              MEDICAL REQUIREMENTS
            </h2>
            <p className="text-xl text-gray-400">
              Please review these conditions before booking
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {medicalConditions.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-900 to-black border border-red-500/20 rounded-xl p-6 hover:border-red-500/40 transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    {Icon && <Icon className="text-red-500 flex-shrink-0 mt-1" size={32} />}
                    <div className="flex-1">
                      <h3 className="text-lg font-normal text-white mb-1">
                        {item.condition}
                      </h3>
                      <p className="text-gray-400 text-sm">
                        Participants with this condition are not permitted to jump
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Have questions? We're here to help!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <a
              href={`tel:${location.phone}`}
              className="flex flex-col items-center p-6 bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-xl hover:border-yellow-400/50 transition-all duration-300 group"
            >
              <Phone className="text-yellow-400 mb-4 group-hover:scale-110 transition-transform" size={32} />
              <p className="text-white font-normal mb-2">Call Us</p>
              <p className="text-gray-400 text-sm">{location.phone}</p>
            </a>

            <a
              href={`mailto:${location.email}`}
              className="flex flex-col items-center p-6 bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-xl hover:border-yellow-400/50 transition-all duration-300 group"
            >
              <Mail className="text-yellow-400 mb-4 group-hover:scale-110 transition-transform" size={32} />
              <p className="text-white font-normal mb-2">Email Us</p>
              <p className="text-gray-400 text-sm">{location.email}</p>
            </a>

            <a
              href={location.mapLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-6 bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-xl hover:border-yellow-400/50 transition-all duration-300 group"
            >
              <MapPin className="text-yellow-400 mb-4 group-hover:scale-110 transition-transform" size={32} />
              <p className="text-white font-normal mb-2">Visit Us</p>
              <p className="text-gray-400 text-sm text-center">{location.address}</p>
            </a>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
              FAQ
            </h2>
            <p className="text-xl text-gray-400">
              Frequently Asked Questions
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-900 to-black border border-yellow-400/20 rounded-xl overflow-hidden hover:border-yellow-400/40 transition-all duration-300"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="text-lg font-normal text-white pr-8">
                    {faq.question}
                  </span>
                  {openFaqIndex === index ? (
                    <ChevronUp className="text-yellow-400 flex-shrink-0" size={24} />
                  ) : (
                    <ChevronDown className="text-yellow-400 flex-shrink-0" size={24} />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-400 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationDetailPage;
